import { Outlet, useNavigate } from 'react-router-dom'
import { useCurrentUser } from '~/lib/auth/use-current-user'
import { ApiHttpError } from '~/lib/api/client'
import { useEffect } from 'react'
import { formatHeaderDate } from '~/lib/time/format'
import { Nav } from './nav'
import { SignOutButton } from './sign-out-button'
import styles from './app-shell.module.scss'

/**
 * Authenticated app shell. Resolves the current user via /api/v1/me;
 * on 401, navigates to /sign-in. Renders the persistent header
 * (date + nav + sign-out) and an Outlet for the child route.
 */
export function AppShell() {
  const { data, error, isLoading } = useCurrentUser()
  const navigate = useNavigate()

  useEffect(() => {
    if (error instanceof ApiHttpError && error.status === 401) {
      navigate('/sign-in', { replace: true })
    }
  }, [error, navigate])

  if (isLoading) {
    return <div className={styles.bootstrapping} aria-hidden />
  }

  if (!data) {
    // Either error already triggered the redirect above, or we're between
    // states. Render nothing rather than flashing chrome.
    return null
  }

  const today = new Date()
  const isoDate = `${today.getFullYear()}-${String(
    today.getMonth() + 1
  ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.dateBlock}>
            <span className={`${styles.dateLabel} mono`}>
              {formatHeaderDate(isoDate)}
            </span>
            <Nav />
          </div>
          <SignOutButton
            displayName={data.user.display_name ?? data.user.slack_user_id}
          />
        </div>
      </header>
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  )
}
